import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { HardHat, MessageSquare, CheckCircle2, Clock, MapPin } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { api } from "@/lib/api";
import { STATUS } from "@/lib/format";

const hhmm = (s) => new Date(s).toLocaleTimeString("it-IT", { hour: "2-digit", minute: "2-digit" });

function SiteCard({ c, i }) {
  return (
    <div className="space-y-2 rounded-xl border p-4" data-testid={`today-site-${i}`}>
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div>
          <p className="font-semibold text-slate-900">{c.name}</p>
          <p className="flex items-center gap-1 text-xs text-slate-500"><MapPin className="h-3.5 w-3.5" />{c.address} · {c.start}–{c.end}</p>
        </div>
        <span className={`rounded-full border px-2 py-0.5 text-[11px] font-medium ${STATUS[c.status]?.cls || ""}`}>{STATUS[c.status]?.label || c.status}</span>
      </div>
      <p className="text-sm" data-testid={`today-site-team-${i}`}><b>Squadra:</b> {c.technician_name ? `${c.technician_name} (responsabile)` : "da assegnare"}{c.helpers.length ? `, ${c.helpers.join(", ")}` : ""}</p>
      <div className="rounded-lg bg-[#f6faf0] px-3 py-2" data-testid={`today-site-plan-${i}`}>
        <p className="text-xs font-semibold text-[#3d6b16]">Programma di lavoro</p>
        <p className="whitespace-pre-line text-sm text-slate-700">{c.work_plan || "Nessun programma per oggi"}</p>
        {c.work_plan && <p className={`mt-1 flex items-center gap-1 text-xs ${c.work_plan_read_at ? "text-[#3d6b16]" : "text-amber-700"}`}>
          {c.work_plan_read_at ? <><CheckCircle2 className="h-3.5 w-3.5" /> Letto da {c.work_plan_read_by}</> : <><Clock className="h-3.5 w-3.5" /> Non ancora letto</>}</p>}
      </div>
      {c.day_done && <p className="text-sm text-slate-700"><b>Resoconto:</b> {c.day_done}{c.day_done_by ? ` (${c.day_done_by})` : ""}</p>}
      <div data-testid={`today-site-messages-${i}`}>
        <p className="flex items-center gap-1 text-xs font-semibold text-slate-500"><MessageSquare className="h-3.5 w-3.5" /> Messaggi di oggi ({c.messages.length})</p>
        {c.messages.map((m, j) => <p key={j} className="text-sm text-slate-700"><span className="text-xs text-slate-400">{hhmm(m.at)}</span> <b>{m.from_name}:</b> {m.text}</p>)}
      </div>
      {c.series_id && <Link to={`/admin/cantieri/${c.series_id}/rendiconto`} className="inline-block text-sm font-semibold text-[#61a229] hover:underline" data-testid={`today-site-report-${i}`}>Apri rendiconto</Link>}
    </div>
  );
}

export function TodaySitesDialog({ open, onOpenChange }) {
  const [rows, setRows] = useState(null);
  const [err, setErr] = useState(false);
  useEffect(() => {
    if (!open) return;
    setRows(null); setErr(false);
    api.get("/admin/dashboard/cantieri-today").then((r) => setRows(r.data)).catch(() => setErr(true));
  }, [open]);
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-2xl" data-testid="today-sites-dialog">
        <DialogHeader><DialogTitle className="flex items-center gap-2 font-display"><HardHat className="h-5 w-5 text-[#61a229]" /> Cantieri di oggi</DialogTitle>
          <DialogDescription>Assegnazioni, squadra, programma di lavoro e messaggi della giornata.</DialogDescription></DialogHeader>
        {err ? <p className="py-6 text-center text-sm text-red-600" data-testid="today-sites-error">Impossibile caricare i cantieri di oggi. Riprova.</p>
          : rows === null ? <p className="text-sm text-slate-500">Caricamento…</p>
          : !rows.length ? <p className="py-6 text-center text-sm text-slate-500" data-testid="today-sites-empty">Nessun cantiere oggi.</p>
          : <div className="space-y-3">{rows.map((c, i) => <SiteCard key={c.id} c={c} i={i} />)}</div>}
      </DialogContent>
    </Dialog>
  );
}
